import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { MessageCircle, Bot, Clock, TrendingUp, Users, Zap, Check, ArrowRight, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const features = [
  {
    icon: Bot,
    title: "AI-Powered Chat Agents",
    description: "Smart chatbots that answer customer questions instantly, 24/7, without human intervention.",
  },
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "Never miss a lead. Your live chat is always ready to engage visitors, even while you sleep.",
  },
  {
    icon: TrendingUp,
    title: "Increase Conversions",
    description: "Engage visitors at the perfect moment and guide them towards making a purchase.",
  },
  {
    icon: Users,
    title: "Reduce Support Load",
    description: "Automate repetitive questions and free up your team for more important tasks.",
  },
  {
    icon: Zap,
    title: "Instant Responses",
    description: "No more waiting. Customers get immediate answers, improving satisfaction and trust.",
  },
  {
    icon: MessageCircle,
    title: "Seamless Handover",
    description: "When needed, conversations smoothly transfer to human agents with full context.",
  },
];

const benefits = [
  {
    stat: "35%",
    label: "Average increase in conversions",
  },
  {
    stat: "80%",
    label: "Reduction in response time",
  },
  {
    stat: "50%",
    label: "Decrease in support tickets",
  },
  {
    stat: "24/7",
    label: "Customer availability",
  },
];

const includedFeatures = [
  "Custom chatbot trained on your business",
  "Integration with your existing website",
  "Multi-channel support: SMS, WhatsApp, Facebook Messenger & more",
  "Lead capture and qualification",
  "Appointment booking capabilities",
  "Analytics and conversation insights",
  "Multi-language support",
  "Mobile-responsive chat widget",
  "CRM integration available",
];

export default function LiveChat() {
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    telephone: "",
    website: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleDemoChange = (e) => {
    setDemoForm({ ...demoForm, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: demoForm.name,
        email: demoForm.email,
        telephone: demoForm.telephone,
        message: `[Live Chat Demo Request]\nWebsite: ${demoForm.website}\n\nPlease set up a live demo of the chatbot on my website.`
      });
      setIsSubmitted(true);
      toast.success("Demo request sent! We'll be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="live-chat-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>24/7 Live Chat Services | Never Miss a Customer Enquiry | Barton</title>
        <meta name="description" content="Professional live chat services for your website. Our team responds to customer enquiries 24/7, converting visitors into leads while you focus on your business." />
        <meta property="og:title" content="24/7 Live Chat Services | Barton" />
        <meta property="og:description" content="Professional live chat for your website. We respond to enquiries 24/7, converting visitors into leads." />
        <meta property="og:url" content="https://barton.uk/services/live-chat" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="24/7 Live Chat Services | Barton" />
        <meta name="twitter:description" content="Professional live chat for your website. We respond to enquiries 24/7." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400 text-sm font-medium mb-6">
              Live Chat Services
            </span>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
              Turn Website Visitors Into{" "}
              <span className="text-gradient">Paying Customers</span>
            </h1>
            
            <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8">
              Install intelligent live chat agents on your website that engage visitors, answer questions instantly, and convert leads while you focus on running your business.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Button
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:bg-gradient-to-r from-blue-500 to-purple-500/90 text-white rounded-full px-8 py-6 text-lg"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-white/20 text-white hover:bg-white/10 bg-transparent"
                onClick={() => document.getElementById('live-demo')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Live Demo
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl p-1">
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80"
                alt="Live chat customer support"
                className="w-full h-[400px] object-cover rounded-2xl"
              />
            </div>
            
            {/* Floating Chat Preview */}
            <div className="absolute -bottom-6 -right-6 bg-[#111111] border border-white/10 rounded-2xl shadow-xl p-4 max-w-[280px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-white">Chat Assistant</p>
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    Online now
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-500 to-purple-500/10 rounded-lg p-3">
                <p className="text-white/80 text-sm">Hi! 👋 How can I help you today?</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section id="live-demo" className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-4">
                See It In Action
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
                Request a Live Demo
              </h2>
              <p className="text-white/60 text-lg">
                Want to see how our chatbot would work on your website? Drop us your details and we'll set up a live demo tailored to your site.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleDemoSubmit} className="bg-[#111111] border border-white/10 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="demo-name" className="text-white">Name *</Label>
                      <Input
                        id="demo-name"
                        name="name"
                        type="text"
                        required
                        value={demoForm.name}
                        onChange={handleDemoChange}
                        placeholder="Your name"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-purple-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="demo-email" className="text-white">Email *</Label>
                      <Input
                        id="demo-email"
                        name="email"
                        type="email"
                        required
                        value={demoForm.email}
                        onChange={handleDemoChange}
                        placeholder="you@company.com"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="demo-telephone" className="text-white">Phone *</Label>
                      <Input
                        id="demo-telephone"
                        name="telephone"
                        type="tel"
                        required
                        value={demoForm.telephone}
                        onChange={handleDemoChange}
                        placeholder="Your phone number"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-purple-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="demo-website" className="text-white">Website URL *</Label>
                      <Input
                        id="demo-website"
                        name="website"
                        type="url"
                        required
                        value={demoForm.website}
                        onChange={handleDemoChange}
                        placeholder="https://yourwebsite.com"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="demo-form-submit"
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:bg-gradient-to-r from-blue-500 to-purple-500/90 text-white rounded-full py-6 text-lg"
                  >
                    {isSubmitting ? "Sending..." : "Request Live Demo"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-[#111111] border border-purple-500/50 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2 text-white">Demo Request Sent!</h3>
                <p className="text-white/60 mb-6">
                  Thanks for your interest! We'll review your website and get back to you within 24 hours with your personalised demo.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/20 text-white hover:bg-white/10 bg-transparent"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-500/10 via-transparent to-purple-500/10 py-12 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="font-heading text-4xl md:text-5xl font-bold mb-2 text-purple-400">
                  {benefit.stat}
                </div>
                <div className="text-white/60 text-sm">{benefit.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
              Why Companies Choose Us...
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Powerful features that help you engage customers and grow your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-[#111111] border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-white">
                  {feature.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-white">
                Everything You Need to Get Started
              </h2>
              <p className="text-white/60 text-lg mb-8">
                We handle the entire setup process, from customizing your chatbot to integrating it seamlessly with your website.
              </p>
              
              <ul className="space-y-4">
                {includedFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="text-white/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#111111] border border-white/10 rounded-2xl p-8">
              <div className="text-center mb-6">
                <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-4">
                  See It In Action
                </span>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">
                  Get A Free Live Demo
                </h3>
                <p className="text-white/60">
                  See exactly how our chatbot would work on your website
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="w-5 h-5 text-purple-400" />
                  Personalised demo on your site
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="w-5 h-5 text-purple-400" />
                  No obligation or commitment
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="w-5 h-5 text-purple-400" />
                  See real results before you decide
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Check className="w-5 h-5 text-purple-400" />
                  Expert walkthrough included
                </li>
              </ul>

              <Button
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:bg-gradient-to-r from-blue-500 to-purple-500/90 text-white rounded-full py-6 text-lg"
                onClick={() => document.getElementById('live-demo')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Request Live Demo <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
            Ready to Boost Your Conversions?
          </h2>
          <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses already using our live chat service to increase sales and improve customer satisfaction.
          </p>
          <Button
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-8 py-6 text-lg"
            onClick={() => document.getElementById('live-demo')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get A Free Live Demo <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
